import { useTranslation } from 'react-i18next';
import ArchiveRestrictedModal from 'components/ArchiveRestrictedModal/ArchiveRestrictedModal';
import EmptyList from 'components/DataSetsEmptyList/components/EmptyList/EmptyList';
import DataSetsEmptyList from 'components/DataSetsEmptyList/DataSetsEmptyList';
import DataSetsItems from 'components/DataSetsItems/DataSetsItems';
import DataSetsListFilters from 'components/DataSetsListFilters/DataSetsListFilters';
import DeactivationInfoModal from 'components/DeactivationModals/DeactivationInfoModal';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import ImportMultipleErrorsModal from 'components/ImportMultipleErrorsModal/ImportMultipleErrorsModal';
import MappingMultipleModal from 'components/MappingMultipleModal/MappingMultipleModal';
import Pagination from 'components/Pagination/Pagination';
import Panel from 'components/Panel/Panel';
import QuestionErrorModal from 'components/QuestionErrorModal/QuestionErrorModal';
import {
  DATA_SET_PAGINATION_COUNT,
  DATA_SETS_SUPPLIERS_HEAD_CELLS,
} from 'constants/dataSets';
import { DELETE_MODAL_TITLES } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import SuppliersItem from './SuppliersItem/SuppliersItem';

import type { Control } from 'react-hook-form';
import type { TDataSetsListSchema } from 'utils/validators';
import type { TUseProductImportRecordsMutation } from '../Products/interfaces/TUseProductImportRecordsMutation';
import type ISuppliersView from './interfaces/ISuppliersView';

import styles from './sass/SuppliersView.module.scss';

function SuppliersView({
  isEmptyList, meta, uploadControl, isUploadFileVisible, isMappingModalVisible,
  onCloseMappingModal, setIsImportErrorsModalVisible, isImportErrorsModalVisible, onDownloadExample, importRecords,
  items, onSortChange, onSearchChange, onSelectAll, isLineMark, onArchive, setIsArchiveModalVisible,
  onArchiveConfirm, isArchiveModalVisible, isArchiveSelectedItemsVisible, isArchiveModalInfoVisible,
  setIsArchiveModalInfoVisible, latestArchivation, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, selectedItems,
  onPageChange, currentTotalItems, onEditRecord, isActionsVisible, onAddRecord, isProfitCenterEmpty, isSupplierEmpty,
  control, naceCodeTreeData, isWithSearch, isLoading, isLazySupplierExampleLoading, onShowArchiveChange,
  activeModal, closeModal, archiveError, locationsCountries,
  onDeleteRecord, isDeleteModalVisible, onCloseDeleteModal, onDeleteConfirm,
  isImportErrorModalVisible, importErrorMessage, onCloseImportErrorModal, onImportError,
}: ISuppliersView) {
  const { t } = useTranslation();

  return (
    <>
      {(isEmptyList && !isWithSearch && !isLoading && !isLazySupplierExampleLoading) ? (
        <DataSetsEmptyList
          title={t('suppliers.title')}
          subTitle={t('suppliers.subTitle')}
          subTitleSecondLine={t('suppliers.subTitleSecondLine')}
          uploadControl={uploadControl}
          isUploadFileVisible={isUploadFileVisible ? !isProfitCenterEmpty : false}
          onDownloadExample={onDownloadExample}
          isProfitCenterEmpty={isProfitCenterEmpty}
          isSupplierEmpty={isSupplierEmpty}
          onShowArchiveChange={onShowArchiveChange}
          control={control}
          titleToggle={t('dataSetsManagement.showArchived')}
          onAddRecord={onAddRecord}
        />
      ) : null}
      {!isEmptyList || isWithSearch ? (
        <div className={styles.root}>
          <Panel className={styles.panel}>
            <div className={styles.content}>
              <DataSetsListFilters
                control={control}
                meta={meta}
                onSearchChange={onSearchChange}
                onArchive={onArchive}
                isArchiveSelectedItemsVisible={isArchiveSelectedItemsVisible}
                selectedItems={selectedItems}
                currentTotalItems={currentTotalItems}
                isActionsVisible={isActionsVisible}
                title={t('suppliers.title')}
                subTitle={t('suppliers.subTitle')}
                onAddRecord={onAddRecord}
                onShowArchiveChange={onShowArchiveChange}
                onDownloadExample={onDownloadExample}
                uploadControl={uploadControl}
              />
              {isEmptyList && isWithSearch && !isLoading ? (
                <EmptyList
                  title={t('suppliers.title')}
                  isProfitCenterEmpty={isProfitCenterEmpty}
                  isSupplierEmpty={isSupplierEmpty}
                  isWithSearch={isWithSearch}
                />
              ) : null}
              {!isEmptyList && (
                <>
                  <DataSetsItems<Control<TDataSetsListSchema>>
                    control={control}
                    onSortChange={onSortChange}
                    onSelectAll={onSelectAll}
                    isLineMark={isLineMark}
                    isActionsVisible={isActionsVisible}
                    headCells={DATA_SETS_SUPPLIERS_HEAD_CELLS}
                    disabledCheckbox={!items?.some((item) => !item.archived)}
                  >
                    {!isLoading && items?.map((item) => (
                      <SuppliersItem
                        key={item.id}
                        item={item}
                        control={control}
                        onArchiveModalInfoOpen={onArchiveModalInfoOpen}
                        onMoveToArchive={onMoveToArchive}
                        onDeArchive={onDeArchive}
                        onEditRecord={onEditRecord}
                        onDeleteRecord={onDeleteRecord}
                        isActionsVisible={isActionsVisible}
                        naceCodeTreeData={naceCodeTreeData}
                        locationsCountries={locationsCountries}
                      />
                    ))}
                  </DataSetsItems>
                  <Pagination
                    count={meta?.count || 0}
                    offset={meta?.offset || 0}
                    limit={DATA_SET_PAGINATION_COUNT}
                    onPageChange={onPageChange}
                  />
                </>
              )}
            </div>
          </Panel>
        </div>
      ) : null}
      <MappingMultipleModal<TUseProductImportRecordsMutation>
        isMappingModalVisible={isMappingModalVisible}
        onCloseMappingModal={onCloseMappingModal}
        setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
        importRecords={importRecords}
        isLoading={isLoading}
        onImportError={onImportError}
      />
      <ImportMultipleErrorsModal
        isImportErrorsModalVisible={isImportErrorsModalVisible}
        setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
        isLoading={isLoading}
        importRecords={importRecords}
      />
      <QuestionErrorModal
        isOpen={isImportErrorModalVisible}
        onClose={onCloseImportErrorModal}
        title={t('suppliers.title')}
        errorLog={importErrorMessage}
      />
      <DeactivationModal
        open={isArchiveModalVisible}
        title={t('profitCentres.archivingReasonTitle')}
        text={t('profitCentres.archivingReasonDescription')}
        onReject={() => { setIsArchiveModalVisible(false); }}
        onSubmit={onArchiveConfirm}
        isLoading={isLoading}
      />
      <DeactivationInfoModal
        open={isArchiveModalInfoVisible}
        title={t('profitCentres.archiveDetails')}
        deactivationInfo={latestArchivation}
        onClose={() => setIsArchiveModalInfoVisible(false)}
        isLoading={isLoading}
      />
      <ArchiveRestrictedModal
        isOpen={activeModal}
        onClose={closeModal}
        archiveError={archiveError}
      />
      <DeleteModal
        open={isDeleteModalVisible}
        onReject={onCloseDeleteModal}
        isLoading={false}
        name=""
        title={DELETE_MODAL_TITLES.DELETE_RECORD}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
}

export default SuppliersView;
